import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import { useToast } from '../contexts/ToastContext';
import { FiUser, FiMail, FiPhone, FiUserCheck, FiTag, FiUpload, FiUsers } from 'react-icons/fi';
import apiClient, { endpoints } from '../api/client';
import { FileUpload } from './FileUpload';

const CreateBulkUserModal = ({ isOpen, onClose, onUserCreated }) => {
    const [file, setFile] = useState(null);
    const [formData, setFormData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const { showSuccess, showError } = useToast();

    const roles = [
        { value: 'confirmed_instructor', label: 'Confirmed Instructor' },
        { value: 'to_be_confirmed', label: 'To Be Confirmed' },
        { value: 'client_in_georgia', label: 'Client In Georgia' },
        { value: 'client_out_georgia', label: 'Client Out Georgia' }
    ];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFile(file);

        if (errors.file) {
            setErrors(prev => ({ ...prev, file: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        }

        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^\+?[1-9]\d{9,14}$/.test(formData.phone.replace(/[\s-()]/g, ''))) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        // Validate form
        // if (!validateForm()) {
        //     return;
        // }

        setLoading(true);
        try {
            // Check authentication state before making the request
            const token = localStorage.getItem('token');
            if (!token) {
                showError('Authentication required. Please log in again.');
                setLoading(false);
                return;
            }

            const text = await file.text();
            const rows = parseCSV(text);

            const usersData = [];
            for (let row of rows.slice(1)) {
                if (!row[0].trim() || !row[1].trim() || !row[2].trim() || !row[3].trim() || !row[4].trim() || !row[5].trim()) {
                    continue;
                }
                
                usersData.push({
                    first_name: row[0],
                    last_name: row[1],
                    email: row[2],
                    phone: row[3].replace(/[\s-()]/g, ''), // Clean phone number
                    mailing_address: row[4],
                    role: row[5],
                });
                console.log(usersData)
            }

            console.log('Creating users with data:', usersData);
            const response = await apiClient.post('/api/users/bulk', { users: usersData });
            console.log('Users creation response:', response);

            if (response.data.success) {
                const createdUsers = response.data.data.users;

                showSuccess('Users created successfully!');
                onUserCreated(response.data.data);
                handleClose();
            } else {
                showError(response.data.message || 'Failed to create users');
            }

            return;
        } catch (error) {
            console.error('Error creating users:', error);
            if (error.response?.data?.message) {
                showError(error.response.data.message);
            } else if (error.response?.status === 401) {
                showError('Authentication failed. Please log in again.');
                // Clear auth state and redirect to login
                localStorage.removeItem('token');
                localStorage.removeItem('admin');
                localStorage.removeItem('refresh_token');
                window.location.href = '/login';
            } else if (error.response?.status === 403) {
                showError('You do not have permission to create users.');
            } else if (error.response?.status === 409) {
                showError('A user with this email already exists');
            } else if (error.response?.status === 400) {
                showError('Invalid users data. Please check all fields and try again.');
            } else if (error.response?.status >= 500) {
                showError('Server error. Please try again later.');
            } else if (error.code === 'NETWORK_ERROR' || !error.response) {
                showError('Network error. Please check your connection and try again.');
            } else {
                showError('Failed to create users. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData([]);
        setErrors({});
        onClose();
    };

    const parseCSV = (text) => {
        const rows = [];
        let cur = "";
        let row = [];
        let inQuotes = false;

        for (let i = 0; i < text.length; i++) {
            const ch = text[i];
            const next = text[i + 1];


            if (ch === '"') {
                if (inQuotes && next === '"') {
                    // escaped quote
                    cur += '"';
                    i++; // skip next
                } else {
                    inQuotes = !inQuotes;
                }
                continue;
            }


            if (!inQuotes && (ch === ',')) {
                row.push(cur);
                cur = "";
                continue;
            }


            if (!inQuotes && (ch === '\n' || (ch === '\r' && text[i + 1] === '\n'))) {
                // handle CRLF and LF
                row.push(cur);
                rows.push(row);
                row = [];
                cur = "";
                if (ch === '\r' && text[i + 1] === '\n') i++; // skip LF after CR
                continue;
            }


            // regular char
            cur += ch;
        }


        // push any trailing data
        if (cur !== "" || row.length > 0) {
            row.push(cur);
            rows.push(row);
        }


        // Trim possible empty trailing row caused by final newline
        if (rows.length > 1 && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === "") {
            rows.pop();
        }


        return rows;
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Add Bulk Users" size="lg">
            <div className="">
                <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-1">
                        <p className="font-bold text-black">
                            CSV TEMPLATE
                        </p>
                        {" "}
                        Make a copy of this sheet, and use it to upload your users.
                        {" "}
                        <a className="text-brand-primary" target="_blank" href="https://docs.google.com/spreadsheets/d/1DYsm-r1_QIBB7FGGLjFzqXqWs5Rz2H20kcz3IuCf7zc/edit?usp=sharing">
                            [Template]
                        </a>
                    </p>

                </div>

                {/* File Upload */}
                <div>
                    <FileUpload file={file} onChange={handleFileChange} fileTypes=".csv,text/csv" tag="CSV" />

                    {errors.file && (
                        <p className="mt-1 text-sm text-red-600">{errors.file}</p>
                    )}
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-6 py-3 mt-6 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 btn-animate"
                >
                    {loading ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                            Creating...
                        </>
                    ) : (
                        <>
                            <FiUsers className="-ml-1 mr-2 h-4 w-4 inline" />
                            Add Users
                        </>
                    )}
                </button>
            </div>
        </Modal>
    );
};

export default CreateBulkUserModal;
